import { useLocationPathname, useNavigate } from '@rocketlane/rocket-router';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useLocalStorage } from 'hooks/use-local-storage';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { APP_SHELL_MODE_STORAGE_KEY, DEFAULT_APP_SHELL_MODE, isPendingSwitchToNitroClawShell, markPendingSwitchToNitroClawShell } from 'utils/app-shell-mode';
import { isTauriApp } from 'utils/is-tauri';
import { isNitroClawProjectPathAllowed } from 'utils/nitro-claw-project-tabs';
const NITRO_CLAW_HOME = `/${ROUTE_PATHS.NITRO_CLAW}`;
/**
 * Persisted Rocketlane ↔ Nitro Claw shell preference (web).
 * Tauri presents the Nitro Claw shell when `AccountFeature.NitroClaw` is on.
 * When the account feature is off, web preference is reset to Rocketlane.
 */ export const useAppShellMode = ()=>{
    const navigate = useNavigate();
    const pathname = useLocationPathname();
    const [isNitroClawEnabled] = useAccountFeature(AccountFeature.NitroClaw);
    const [mode, setMode] = useLocalStorage(APP_SHELL_MODE_STORAGE_KEY, DEFAULT_APP_SHELL_MODE);
    useEffect(()=>{
        if (!isTauriApp() && !isNitroClawEnabled && mode === 'nitro-claw') {
            markPendingSwitchToNitroClawShell(false);
            setMode(DEFAULT_APP_SHELL_MODE);
        }
    }, [
        isNitroClawEnabled,
        mode,
        setMode
    ]);
    useLayoutEffect(()=>{
        if (!isPendingSwitchToNitroClawShell()) {
            return;
        }
        if (isNitroClawProjectPathAllowed(pathname) === false) {
            return;
        }
        markPendingSwitchToNitroClawShell(false);
        if (mode !== 'nitro-claw') {
            setMode('nitro-claw');
        }
    }, [
        pathname,
        mode,
        setMode
    ]);
    const isNitroClawShellActive = isNitroClawEnabled && (isTauriApp() || mode === 'nitro-claw');
    const switchToAppShell = useCallback((next)=>{
        if (next === 'nitro-claw') {
            markPendingSwitchToNitroClawShell(true);
            navigate({
                pathname: NITRO_CLAW_HOME,
                search: '',
                hash: ''
            });
            if (isNitroClawProjectPathAllowed(pathname) !== false) {
                markPendingSwitchToNitroClawShell(false);
                if (mode !== 'nitro-claw') {
                    setMode('nitro-claw');
                }
            }
            return;
        }
        markPendingSwitchToNitroClawShell(false);
        if (next === mode) {
            return;
        }
        setMode(next);
        navigate({
            pathname: ROUTE_PATHS.ROOT,
            search: '',
            hash: ''
        });
    }, [
        mode,
        navigate,
        pathname,
        setMode
    ]);
    return {
        mode,
        setMode,
        switchToAppShell,
        isNitroClawShellActive,
        isNitroClawEnabled
    };
};
